import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect, useCallback, useRef } from "react";
import Header from "../../../components/Header";
import {
  accountsmanagement,
  caseManagementUrl,
  clientManagementUrl,
} from "../../../config";
import {
  allCasesView,
  currency,
  allAccountSubCategories,
  allAccounts,
  allModeOfReceipts,
  allFeeNotesView,
} from "../../../data/Axios/queries";
import {
  fetchDataEngine,
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../data/Axios/DynamicService";
import { filterAccountSubCategoriesByUseCase } from "../../../data/finance/accountCategoryRules";

const base_url = accountsmanagement.uri;
const client_base_url = clientManagementUrl.uri;
const case_base_url = caseManagementUrl.uri;

const clientTypeOptions = [
  { value: 1, label: "Individual" },
  { value: 2, label: "Corporate" },
];

/**
 * Dedicated component to synchronize Formik values with top-level state.
 * This ensures the useEffect is called inside a valid React functional component,
 * resolving the "rules-of-hooks" error.
 */
const ValueSynchronizer = ({ values, setFormValuesMirror }) => {
  useEffect(() => {
    // This safely updates the outside state whenever these core Formik values change.
    setFormValuesMirror({
      clientId: values.clientId,
      clientTypeId: values.clientTypeId,
      caseId: values.caseId,
      currencyId: values.currencyId,
    });
  }, [values.clientId, values.clientTypeId, values.currencyId, values.caseId, setFormValuesMirror]);

  return null; // This component is only for side effects, it renders nothing.
};

const ClientReceiptEntryForm = (props) => {
  const { isEditing = false, data = {}, onClose, refetchData } = props;

  const formikRef = useRef();
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id) || null;

  // --- Core State ---
  const [modeOfReceipts, setModeOfReceipts] = useState([]);
  const [clients, setClients] = useState([]); // Client Autocomplete options based on clientTypeId
  const [cases, setCases] = useState([]); // Case dropdown options
  const [accountSubCategories, setAccountSubCategories] = useState([]);
  const [accounts, setAccounts] = useState([]); // Account dropdown options (filtered)
  const [currencies, setCurrencies] = useState([]);
  const [allIndividualClientsData, setAllIndividualClientsData] = useState([]);
  const [allCorporateClientsData, setAllCorporateClientsData] = useState([]);
  const [allCasesData, setAllCasesData] = useState([]); // All cases fetched from API
  const [allAccountsData, setAllAccountsData] = useState([]); // All accounts fetched from API
  const [allFeeNotes, setAllFeeNotes] = useState([]);
  const [filteredFeeNotes, setFilteredFeeNotes] = useState([]);

  // State to hold the pre-selected client object for Autocomplete initialization (Fix for editing mode)
  const [selectedClientInitial, setSelectedClientInitial] = useState(null);

  // 1. State mirror to track reactive Formik values outside the render prop
  const [formValuesMirror, setFormValuesMirror] = useState({
    clientId: data?.clientId || null,
    clientTypeId: data?.clientTypeId || null,
    caseId: data?.caseId || null,
    currencyId: data?.currencyId || null,
  });

  const initialValues = {
    id: data?.id || null,
    receiptDate: data?.receiptDate || new Date().toISOString().split("T")[0],
    clientTypeId: data?.clientTypeId || null,
    clientId: data?.clientId || null,
    caseId: data?.caseId || null,
    feeNoteId: data?.feeNoteId || null,
    modeOfReceiptId: data?.modeOfReceiptId || null,
    referenceNumber: data?.referenceNumber || null,
    accountSubCategoryId: data?.accountSubCategoryId || null,
    accountId: data?.accountId || null,
    currencyId: data?.currencyId || null,
    exchangeRate: data?.exchangeRate || 1.0,
    outstandingAmount: data?.outstandingAmount || 0,
    totalAmount: data?.totalAmount || 0,
    companyId: data?.companyId || null,
    createdBy: data?.createdBy || null,
    updatedBy: data?.updatedBy || null,
    isActive: data?.isActive !== undefined ? data.isActive : 1,
  };

  const filterClients = useCallback((clientTypeId, individuals, corporates) => {
    const typeId = parseInt(clientTypeId);
    if (typeId === 1) {
      return individuals.map((c) => ({
        id: c.id,
        name: c.fullName,
        clientTypeId: 1,
      }));
    } else if (typeId === 2) {
      return corporates.map((c) => ({
        id: c.id,
        name: c.companyName,
        clientTypeId: 2,
      }));
    }
    return [];
  }, []);

  const mapAccount = useCallback((acc) => ({
    id: acc.id,
    name: `${acc.accountName} (${acc.accountNumber})`,
    accountName: acc.accountName,
    accountNumber: acc.accountNumber,
  }), []);

  const filterAccountsBySubCategory = useCallback(
    (subCategoryId, allAccountsData) => {
      return allAccountsData
        .filter((acc) => acc.accountSubCategoryId === parseInt(subCategoryId))
        .map(mapAccount);
    },
    [mapAccount]
  );

  // Dedicated function to ensure cases are always fresh
  const fetchAllCases = useCallback(async () => {
    try {
      const caseData = await graphqlQuery(allCasesView, case_base_url);
      setAllCasesData(caseData || []);
    } catch (error) {
      console.error("Error fetching all cases:", error);
      setAllCasesData([]);
    }
  }, []);

  // ---------------------------
  // NEW: handleAccountSubCategoryChange
  // ---------------------------
  // Uses fetchDataEngine to fetch accounts filtered by the selected subcategory,
  // maps them similarly to mapAccount, sets accounts state and resets accountId.
  const handleAccountSubCategoryChange = async (event, setFieldValue) => {
    const selectedSubCategoryId = event?.target?.value;
    try {
      const fetchedAccounts = await fetchDataEngine(
        "allAccountsByColumnValues",
        "accounts",
        { accountSubcategoryId: selectedSubCategoryId },
        ["id", "accountNumber", "accountName"],
        base_url
      );

      const mappedAccounts = (fetchedAccounts || []).map((acc) => mapAccount(acc));
      setAccounts(mappedAccounts);

      // set the form field for account subcategory and clear account selection
      if (setFieldValue) {
        setFieldValue("accountSubCategoryId", selectedSubCategoryId);
        setFieldValue("accountId", null);
      }
    } catch (error) {
      console.error("Error fetching accounts by subcategory:", error);
      // keep existing accounts if fetching fails
      if (setFieldValue) {
        setFieldValue("accountSubCategoryId", selectedSubCategoryId);
        setFieldValue("accountId", null);
      }
    }
  };

  // 🟩 Fetch all required data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // --- 1. Fetch Master Data ---
        const modeData = await graphqlQuery(allModeOfReceipts, base_url);
        setModeOfReceipts(modeData || []);

        const subCatData = await graphqlQuery(allAccountSubCategories, base_url);
        const filteredSubCats = filterAccountSubCategoriesByUseCase(
          subCatData || [],
          "CLIENT_RECEIPT_SETTLEMENT"
        );
        setAccountSubCategories(filteredSubCats);
        const accountsData = await graphqlQuery(allAccounts, base_url);
        setAllAccountsData(accountsData || []);

        const currencyData = await graphqlQuery(currency, base_url);
        setCurrencies(currencyData || []);

        const feeNotesData = await graphqlQuery(allFeeNotesView, base_url);
        setAllFeeNotes(feeNotesData || []);
        setFilteredFeeNotes(feeNotesData || []);

        // --- 2. Initialize Accounts Dropdown Options (Robust for Edit Mode) ---
        let initialAccountOptions = accountsData.map(mapAccount);

        if (isEditing && data?.accountSubCategoryId) {
          const accountIdInt = parseInt(data.accountId);

          // 1. Apply the subcategory filter
          let filtered = accountsData
            .filter(acc => acc.accountSubCategoryId === parseInt(data.accountSubCategoryId))
            .map(mapAccount);

          // 2. Check if the pre-selected account exists in the filtered list
          const selectedAccountMatch = filtered.find(acc => acc.id === accountIdInt);

          // 3. If the selected account is NOT in the filtered list, add it as a fallback option.
          if (!selectedAccountMatch && accountIdInt) {
            const fallback = accountsData.find(acc => acc.id === accountIdInt);
            if (fallback) {
              filtered = [...filtered, mapAccount(fallback)];
            }
          }
          initialAccountOptions = filtered;
        }

        // Set initial accounts state for the Autocomplete options
        setAccounts(initialAccountOptions || []);

        // --- 3. Fetch Client Data ---
        const inputFields = { isActive: 1 };
        const individualData = await fetchDataEngine(
          "allIndividualClients",
          "clientModel",
          inputFields,
          ["id", "fullName"],
          client_base_url
        );
        setAllIndividualClientsData(individualData || []);

        const corporateData = await fetchDataEngine(
          "allCorporateClients",
          "clientModel",
          inputFields,
          ["id", "companyName"],
          client_base_url
        );
        setAllCorporateClientsData(corporateData || []);

        // --- 4. Client Initialization FIX (for Autocomplete in Edit Mode) ---
        if (isEditing && data?.clientId) {
          const clientList = data.clientTypeId === 1 ? individualData : corporateData;
          const initialClient = clientList.find(c => c.id === data.clientId);
          if (initialClient) {
            setSelectedClientInitial({
              id: initialClient.id,
              name: initialClient.fullName || initialClient.companyName,
              clientTypeId: data.clientTypeId,
            });
          }
        }

        // --- 5. Case Data ---
        await fetchAllCases();

      } catch (error) {
        console.error("Error fetching form data:", error);
      }
    };
    fetchData();
  }, [fetchAllCases, isEditing, data, mapAccount]);

  // Effect to manage client options for Autocomplete (runs on load for editing, or when client data changes)
  useEffect(() => {
    // If formikRef is not available yet, default to data?.clientTypeId for initial load
    const targetClientTypeId = data?.clientTypeId || formValuesMirror.clientTypeId;

    // Check if the required data is present
    if (targetClientTypeId && allIndividualClientsData.length && allCorporateClientsData.length) {
      const filteredClients = filterClients(
        targetClientTypeId,
        allIndividualClientsData,
        allCorporateClientsData
      );
      setClients(filteredClients);
    } else {
      // Keep clients empty in creation mode until client type is selected
      setClients([]);
    }
  }, [data, allIndividualClientsData, allCorporateClientsData, filterClients, formValuesMirror.clientTypeId]); // Use mirror for reactivity

  // 2. Case Filtering Effect - Uses State Mirror as dependency (prevents hooks error)
  useEffect(() => {
    const { clientId, clientTypeId } = formValuesMirror;

    if (clientId && clientTypeId) {
      const filteredCases = allCasesData.filter(
        (c) =>
          c.clientId === parseInt(clientId) &&
          c.clientTypeId === parseInt(clientTypeId)
      );
      setCases(filteredCases);

      // Use formikRef.current to update caseId outside the render prop
      if (formikRef.current && !filteredCases.find((c) => c.id === formikRef.current.values.caseId)) {
        formikRef.current.setFieldValue("caseId", null);
      }
    } else {
      setCases([]);
      if (formikRef.current) {
        formikRef.current.setFieldValue("caseId", null);
      }
    }
  }, [allCasesData, formValuesMirror, formikRef]);

  // 3. Fee Note Filtering Effect - Uses State Mirror as dependency (prevents hooks error)
  useEffect(() => {
    const { clientId } = formValuesMirror;

    if (clientId) {
      const filtered = allFeeNotes.filter(
        fn => fn.clientId === parseInt(clientId)
      );
      setFilteredFeeNotes(filtered);

      // Use formikRef.current to update feeNoteId outside the render prop
      if (formikRef.current && !filtered.find(fn => fn.id === formikRef.current.values.feeNoteId)) {
        formikRef.current.setFieldValue("feeNoteId", null);
      }
    } else {
      setFilteredFeeNotes([]);
      if (formikRef.current) {
        formikRef.current.setFieldValue("feeNoteId", null);
      }
    }
  }, [allFeeNotes, formValuesMirror, formikRef]);

  // 🟩 Effect for filtering accounts by subcategory (Account Dropdown Options) - runs when subcategory changes
  useEffect(() => {
    const formik = formikRef.current;
    if (!formik) return;
    const { values, setFieldValue } = formik;

    // This effect handles filtering when the user interacts with the subcategory field.
    if (values.accountSubCategoryId && allAccountsData.length) {
      const filteredAccounts = filterAccountsBySubCategory(
        values.accountSubCategoryId,
        allAccountsData
      );
      setAccounts(filteredAccounts);
      // Check if the current account is still valid after filtering
      if (!filteredAccounts.find((acc) => acc.id === values.accountId)) {
        // If accountId is present but not in the new filtered list, clear it
        if (values.accountId !== null) {
          setFieldValue("accountId", null);
        }
      }
    }
  }, [allAccountsData, filterAccountsBySubCategory, formikRef]);

  // 🟩 Effect for setting Exchange Rate
  useEffect(() => {
    const { currencyId } = formValuesMirror; // Use mirror for reactivity
    const formik = formikRef.current;
    if (!formik) return;

    if (currencyId) {
      const selectedCurrency = currencies.find(
        (curr) => curr.id === parseInt(currencyId)
      );
      formik.setFieldValue("exchangeRate", selectedCurrency?.exchangeRate || 1.0);
    }
  }, [currencies, formValuesMirror, formikRef]);

  // 🟩 Effect for setting Outstanding Amount from Fee Note Balance Column
  useEffect(() => {
    const formik = formikRef.current;
    if (!formik) return;

    const feeNoteId = formik.values.feeNoteId;
    
    if (feeNoteId) {
      const selectedFeeNote = allFeeNotes.find(
        (fn) => fn.id === parseInt(feeNoteId)
      );
      if (selectedFeeNote && selectedFeeNote.balance !== undefined) {
        // Parse balance as a number to ensure proper handling
        const balanceValue = parseFloat(selectedFeeNote.balance) || 0;
        formik.setFieldValue("outstandingAmount", balanceValue);
      } else {
        formik.setFieldValue("outstandingAmount", 0);
      }
    } else {
      formik.setFieldValue("outstandingAmount", 0);
    }
  }, [allFeeNotes, formikRef, formValuesMirror.clientId]); // Added clientId to dependencies


  const validationSchema = yup.object().shape({
    clientTypeId: yup.mixed().required("Client Type is required"),
    clientId: yup.mixed().required("Client is required"),
    modeOfReceiptId: yup.mixed().required("Mode of Receipt is required"),
    accountSubCategoryId: yup.mixed().required("Account Subcategory is required"),
    // Using mixed to handle potential numeric/null initial values from data
    accountId: yup.mixed().required("Account is required"),
    currencyId: yup.mixed().required("Currency is required"),

  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const {
        clientTypeId,
        clientId,
        caseId,
        feeNoteId,
        modeOfReceiptId,
        referenceNumber,
        accountSubCategoryId,
        accountId,
        currencyId,
        exchangeRate,
        totalAmount,
        receiptDate,
        companyId,
        isActive = 1,
        // outstandingAmount is excluded - not submitted to backend
      } = values;

      const selectedMode = modeOfReceipts.find(
        (m) => m.id === parseInt(modeOfReceiptId)
      );
      const requiresReference = selectedMode?.name !== "Cash";

      // Manual validation for referenceNumber based on mode
      if (requiresReference && !referenceNumber) {
        throw new Error(
          `Reference number is required for ${selectedMode?.name}`
        );
      }

      const baseMutationData = {
        clientTypeId: clientTypeId ? parseInt(clientTypeId) : null,
        clientId: parseInt(clientId),
        caseId: caseId ? parseInt(caseId) : null,
        feeNoteId: feeNoteId ? parseInt(feeNoteId) : null,
        modeOfReceiptId: parseInt(modeOfReceiptId),
        referenceNumber: referenceNumber || null,
        accountSubCategoryId: parseInt(accountSubCategoryId),
        accountId: parseInt(accountId),
        currencyId: parseInt(currencyId),
        exchangeRate: parseFloat(exchangeRate),
        totalAmount: parseFloat(totalAmount),
        receiptDate,
        companyId,
        isActive,
      };

      let response = null;

      if (isEditing) {
        const mutationData = { ...baseMutationData, id: data?.id, updatedBy: userId };
        // FIX: Corrected input variable name from 'newReceipt' to 'updatedReceipt'
        response = await generateAndExecuteMutation(
          "updateReceipt",
          mutationData,
          "updatedReceipt", // The correct input name in the mutation template
          base_url
        );
      } else {
        const mutationData = { ...baseMutationData, createdBy: userId };
        response = await generateAndExecuteMutation(
          "createReceipt",
          mutationData,
          "newReceipt",
          base_url
        );
      }

      if (response) {
        onClose();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Receipt ${isEditing ? "updated" : "created"} successfully.`,
        }).then(() => {
          refetchData();

        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text:
          error.message ||
          "An error occurred while processing your request. Please try again later.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit Receipt" : "Create Receipt"}
        subtitle={
          isEditing ? "Edit an Existing Receipt" : "Create a New Receipt"
        }
      />

      <Formik
        innerRef={formikRef}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => {

          // ----------------------------------------------------------------------
          // Autocomplete Value Object Calculation
          // ----------------------------------------------------------------------

          // Account Dropdown Value FIX: Search only the *filtered* accounts state
          const accountIdInt = values.accountId ? parseInt(values.accountId) : null;
          const selectedAccountOption = accounts.find(
            // Ensure type safety in comparison
            (acc) => parseInt(acc.id) === accountIdInt
          ) || null;

          // Case Dropdown Value FIX: Ensure type safety for edit mode
          const caseIdInt = values.caseId ? parseInt(values.caseId) : null;
          const selectedCaseOption = cases.find(
            (c) => c.id === caseIdInt
          ) || null;

          // Fee Note Dropdown Value FIX: Ensure type safety for edit mode
          const feeNoteIdInt = values.feeNoteId ? parseInt(values.feeNoteId) : null;
          const selectedFeeNoteOption = filteredFeeNotes.find(
            (fn) => fn.id === feeNoteIdInt
          ) || null;

          // Client Autocomplete Value Determination
          const selectedClientOption = clients.find((c) => c.id === values.clientId);

          const clientAutocompleteValue = selectedClientOption
            ? selectedClientOption
            : (values.clientId ? selectedClientInitial : null);
          // ----------------------------------------------------------------------


          const selectedMode = modeOfReceipts.find(
            (m) => m.id === parseInt(values.modeOfReceiptId || 0)
          );
          const showReferenceField = selectedMode?.name !== "Cash";

          return (
            <form onSubmit={handleSubmit}>
              {/* SYNCHRONIZER: Safely calls useEffect to update formValuesMirror */}
              <ValueSynchronizer
                values={values}
                setFormValuesMirror={setFormValuesMirror}
              />
              <Box
                display="grid"
                gap="30px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                  "& > div": {
                    gridColumn: isNonMobile ? undefined : "span 4",
                  },
                }}
              >

                {/* Receipt Date
                <TextField
                  fullWidth
                  variant="filled"
                  type="date"
                  label="Receipt Date *"
                  name="receiptDate"
                  value={values.receiptDate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.receiptDate && !!errors.receiptDate}
                  helperText={touched.receiptDate && errors.receiptDate}
                  sx={{ gridColumn: "span 2" }}
                />


                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Total Amount *"
                  name="totalAmount"
                  value={values.totalAmount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isEditing}
                  error={touched.totalAmount && !!errors.totalAmount}
                  helperText={touched.totalAmount && errors.totalAmount}
                  sx={{ gridColumn: "span 2" }}
                /> */}

                {/* Client Type Dropdown */}
                <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                  <InputLabel>Client Type *</InputLabel>
                  <Select
                    name="clientTypeId"
                    value={values.clientTypeId || ""}
                    onChange={(e) => {
                      const newTypeId = e.target.value;
                      setFieldValue("clientTypeId", newTypeId);
                      setFieldValue("clientId", null);
                      setFieldValue("caseId", null);
                      setFieldValue("feeNoteId", null);
                      setClients(
                        filterClients(newTypeId, allIndividualClientsData, allCorporateClientsData)
                      );
                      // Clearing dependencies when client type changes
                      setCases([]);
                      setFilteredFeeNotes([]);
                    }}
                    onBlur={handleBlur}
                    error={touched.clientTypeId && !!errors.clientTypeId}
                  >
                    {clientTypeOptions.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Client Dropdown */}
                <Autocomplete
                  fullWidth
                  options={clients}
                  getOptionLabel={(option) => option.name || ""}
                  value={clientAutocompleteValue || null}
                  onChange={(e, newValue) => {
                    // This change triggers the reactive state mirror update via ValueSynchronizer
                    setFieldValue("clientId", newValue?.id || null);
                    setFieldValue("caseId", null);
                    setFieldValue("feeNoteId", null);
                  }}
                  disabled={!values.clientTypeId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="filled"
                      label="Client *"
                      error={touched.clientId && !!errors.clientId}
                      helperText={touched.clientId && errors.clientId}
                    />
                  )}
                  sx={{ gridColumn: "span 2" }}
                />

                {/* Case Dropdown (Optional) */}
                <Autocomplete
                  fullWidth
                  options={cases}
                  getOptionLabel={(option) => option.title || option.caseNumber || option.id.toString()}
                  value={selectedCaseOption}
                  onChange={(e, newValue) => setFieldValue("caseId", newValue?.id || null)}
                  disabled={!values.clientId || cases.length === 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="filled"
                      label="Case (Optional)"
                    />
                  )}
                  sx={{ gridColumn: "span 2" }}
                />

                {/* Fee Note Dropdown (Optional) */}
                <Autocomplete
                  fullWidth
                  options={filteredFeeNotes}
                  getOptionLabel={(option) =>
                    `${option.documentTypeName} - ${option.issueDate} (${option.amount})`
                  }
                  value={selectedFeeNoteOption}
                  onChange={(e, newValue) => {
                    setFieldValue("feeNoteId", newValue?.id || null);
                    // Auto-populate outstanding amount from fee note balance
                    if (newValue && newValue.balance !== undefined) {
                      const balanceValue = parseFloat(newValue.balance) || 0;
                      setFieldValue("outstandingAmount", balanceValue);
                    } else {
                      setFieldValue("outstandingAmount", 0);
                    }
                  }}
                  disabled={!values.clientId || filteredFeeNotes.length === 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="filled"
                      label="Fee Note (Optional)"
                    />
                  )}
                  sx={{ gridColumn: "span 2" }}
                />

                {/* Outstanding Amount (Read-only) - Auto-populated from Fee Note Balance */}
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Outstanding Balance"
                  value={values.outstandingAmount}
                  name="outstandingAmount"
                  InputProps={{ 
                    readOnly: true,
                    startAdornment: values.outstandingAmount > 0 ? ' ' : ''
                  }}
                  disabled
                  sx={{ gridColumn: "span 2" }}
                />

                {/* Mode of Receipt Dropdown */}
                <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                  <InputLabel>Mode of Receipt *</InputLabel>
                  <Select
                    name="modeOfReceiptId"
                    value={values.modeOfReceiptId || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.modeOfReceiptId && !!errors.modeOfReceiptId}
                  >
                    {modeOfReceipts.map((mode) => (
                      <MenuItem key={mode.id} value={mode.id}>
                        {mode.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Reference Number (Dynamic based on mode) */}
                {showReferenceField && (
                  <TextField
                    fullWidth
                    variant="filled"
                    label={`Reference Number ${showReferenceField ? '*' : ''} (${selectedMode?.name === 'Cheque' ? 'Cheque No' :
                      selectedMode?.name === 'Mpesa' ? 'Mpesa Code/Tel No' : 'Bank Ref No'})`}
                    name="referenceNumber"
                    value={values.referenceNumber || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    sx={{ gridColumn: "span 2" }}
                  />
                )}
                {!showReferenceField && (
                  <Box sx={{ gridColumn: "span 2" }} />
                )}

                {/* Account Subcategory Dropdown - UPDATED to use PaymentVoucher style handler */}
                <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                  <InputLabel>Account Subcategory *</InputLabel>
                  <Select
                    name="accountSubCategoryId"
                    value={values.accountSubCategoryId || ""}
                    onChange={(e) => {
                      // Note: handler will set both accountSubCategoryId and reset accountId.
                      handleAccountSubCategoryChange(e, setFieldValue);
                    }}
                    onBlur={handleBlur}
                    error={touched.accountSubCategoryId && !!errors.accountSubCategoryId}
                  >
                    {accountSubCategories.map((subCat) => (
                      <MenuItem key={subCat.id} value={subCat.id}>
                        {subCat.accountCategoryName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Account Dropdown - uses filtered `accounts` */}
                <Autocomplete
                  fullWidth
                  options={accounts}
                  getOptionLabel={(option) => option.name || ""}
                  value={selectedAccountOption}
                  onChange={(e, newValue) => setFieldValue("accountId", newValue?.id || null)}
                  disabled={!values.accountSubCategoryId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="filled"
                      label="Account *"
                      error={touched.accountId && !!errors.accountId}
                      helperText={touched.accountId && errors.accountId}
                    />
                  )}
                  sx={{ gridColumn: "span 2" }}
                />

                {/* Currency Dropdown */}
                <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 2" }}>
                  <InputLabel>Currency *</InputLabel>
                  <Select
                    name="currencyId"
                    value={values.currencyId || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.currencyId && !!errors.currencyId}
                  >
                    {currencies.map((curr) => (
                      <MenuItem key={curr.id} value={curr.id}>
                        {curr.currencyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Exchange Rate (Read-only) */}
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Exchange Rate"
                  value={values.exchangeRate}
                  name="exchangeRate"
                  InputProps={{ readOnly: true }}
                  disabled
                  sx={{ gridColumn: "span 2" }}
                />

              </Box>

              <Box display="flex" justifyContent="end" mt="20px">
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : isEditing
                      ? "Update Receipt"
                      : "Create Receipt"}
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default ClientReceiptEntryForm;
